// src/components/qrTheme/QrThemeDialog.jsx
//
// « Apparence » on the owner's dashboard: the style of the « Afficher le
// QR » page (five layouts, backend/utils/qrLayouts.ts; default « Météo d'abord »)
// and its language (backend/utils/qrPageI18n.ts; default from the facility's country),
// and the background colour and font (backend/utils/qrTheme.ts) - used by
// the « Bistrot » QR page and by the rating page whatever the style - with
// a live preview. Nothing chosen = the defaults.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Alert, Tooltip, TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { getQrTheme, saveQrTheme } from '../../utils/qrThemeApi';

// The fonts' own stylesheet, once, so the list and the preview show them.
function useFontsLoaded(fonts) {
  useEffect(() => {
    const families = (fonts || []).filter((f) => f.googleFamily).map((f) => `family=${f.googleFamily}`);
    if (!families.length || document.getElementById('qr-theme-fonts')) return;
    const link = document.createElement('link');
    link.id = 'qr-theme-fonts';
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
    document.head.appendChild(link);
  }, [fonts]);
}

function Preview({ color, font, name }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="qr-theme-preview"
      sx={{ bgcolor: color.bg, color: color.ink, fontFamily: font.stack, borderRadius: 2, p: 2.5, border: '1px solid', borderColor: 'divider' }}
    >
      <Typography sx={{ fontFamily: 'inherit', fontWeight: 700, fontSize: '1.4rem', textAlign: 'center', mb: 1.5 }}>{name}</Typography>
      <Box sx={{ bgcolor: color.panel, border: `1px solid ${color.border}`, borderRadius: 1.5, p: 1.5 }}>
        <Typography sx={{ fontFamily: 'inherit', fontWeight: 600 }}>{t('qrTheme.previewRate')}</Typography>
        <Box sx={{ fontSize: 26, letterSpacing: 2, my: 0.5 }}>
          <span style={{ color: '#F5B301' }}>★★★★</span><span style={{ color: color.muted, opacity: 0.35 }}>★</span>
        </Box>
        <Typography sx={{ fontFamily: 'inherit', fontSize: '.85rem', color: color.muted, mb: 1 }}>{t('qrTheme.previewHint')}</Typography>
        <Box component="span" sx={{ display: 'inline-block', bgcolor: color.accent, color: color.onAccent, borderRadius: 1, px: 2, py: 1, fontWeight: 600 }}>
          {t('qrTheme.previewButton')}
        </Box>
      </Box>
    </Box>
  );
}

export default function QrThemeDialog({ open, facility, token, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);       // the backend's answer: lists and defaults
  const [color, setColor] = useState(null);     // chosen ids, null = default
  const [font, setFont] = useState(null);
  const [layout, setLayout] = useState(null);
  const [language, setLanguage] = useState(null); // null = automatic, from the country
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useFontsLoaded(data?.fonts);

  const facilityId = facility?.id;
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    if (!open || !facilityId) return;
    setData(null); setError(''); setLoadFailed(false);
    getQrTheme(facilityId, token)
      .then((d) => { setData(d); setColor(d.color); setFont(d.font); setLayout(d.layout ?? null); setLanguage(d.language ?? null); })
      .catch(() => setLoadFailed(true));
  }, [open, facilityId, token]);

  const colorId = color || data?.defaults.color;
  const fontId = font || data?.defaults.font;
  const defaultLayout = data?.defaults.layout || 'meteo';
  const layoutId = layout || defaultLayout;
  const current = data && {
    color: data.colors.find((c) => c.id === colorId),
    font: data.fonts.find((f) => f.id === fontId),
  };

  const languageName = (id) => data?.languages?.find((x) => x.id === id)?.label || id;

  const save = async (c, f, l, lang) => {
    setSaving(true); setError('');
    try {
      await saveQrTheme(facilityId, c, f, l, lang, token);
      onClose(true);
    } catch {
      setError(t('qrTheme.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('qrTheme.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('qrTheme.intro')}</Typography>
        {loadFailed && <Alert severity="error" sx={{ mb: 2 }}>{t('qrTheme.loadError')}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data && !loadFailed && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
        {current && (
          <>
            {data.layouts?.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('qrTheme.layout')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 1.5, mb: 2.5 }}>
                  {data.layouts.map((l) => (
                    <Box
                      key={l.id}
                      component="button"
                      type="button"
                      aria-pressed={l.id === layoutId}
                      aria-label={t(`qrTheme.layouts.${l.id}`, l.label)}
                      onClick={() => setLayout(l.id === defaultLayout ? null : l.id)}
                      sx={{
                        p: 0.75, cursor: 'pointer', bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center',
                        border: '2px solid', borderColor: l.id === layoutId ? 'primary.main' : 'divider',
                      }}
                    >
                      <Box component="img" src={`/qr-layouts/${l.id}.jpg`} alt="" sx={{ width: '100%', aspectRatio: '5 / 9', objectFit: 'cover', objectPosition: 'top', borderRadius: 1, display: 'block' }} />
                      <Typography sx={{ fontSize: '.8rem', fontWeight: 600, mt: 0.5 }}>{t(`qrTheme.layouts.${l.id}`, l.label)}</Typography>
                    </Box>
                  ))}
                </Box>
                {layoutId !== 'bistrot' && (
                  <Alert severity="info" sx={{ mb: 2 }}>{t('qrTheme.ownLook')}</Alert>
                )}
              </>
            )}

            {data.languages?.length > 0 && (
              <TextField
                select
                fullWidth
                size="small"
                label={t('qrTheme.language')}
                value={language || ''}
                onChange={(e) => setLanguage(e.target.value || null)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                helperText={t('qrTheme.languageHint')}
                sx={{ mb: 2.5 }}
              >
                <option value="">
                  {t('qrTheme.languageAuto', {
                    language: languageName(data.autoLanguage),
                    country: data.country ? t('qrTheme.languageCountry', { country: data.country }) : '',
                  })}
                </option>
                {data.languages.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </TextField>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('qrTheme.color')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
              {data.colors.map((c) => (
                <Tooltip key={c.id} title={c.label}>
                  <Box
                    component="button"
                    type="button"
                    aria-label={c.label}
                    aria-pressed={c.id === colorId}
                    onClick={() => setColor(c.id === data.defaults.color ? null : c.id)}
                    sx={{
                      width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', position: 'relative', p: 0,
                      bgcolor: c.bg, border: '1px solid', borderColor: 'divider',
                      outline: c.id === colorId ? '3px solid' : 'none', outlineColor: 'primary.main', outlineOffset: 2,
                    }}
                  >
                    <Box sx={{ position: 'absolute', right: 4, bottom: 4, width: 12, height: 12, borderRadius: '50%', bgcolor: c.accent }} />
                    {c.id === colorId && <CheckIcon sx={{ color: c.ink, fontSize: 20, position: 'absolute', top: 11, left: 11 }} />}
                  </Box>
                </Tooltip>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('qrTheme.font')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
              {data.fonts.map((f) => (
                <Button
                  key={f.id}
                  size="small"
                  variant={f.id === fontId ? 'contained' : 'outlined'}
                  aria-pressed={f.id === fontId}
                  onClick={() => setFont(f.id === data.defaults.font ? null : f.id)}
                  sx={{ fontFamily: f.stack, textTransform: 'none', fontSize: '.95rem' }}
                >
                  {f.label}
                </Button>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('qrTheme.preview')}</Typography>
            {/* The dashboard clears `facility` as the dialog starts closing: no name then. */}
            <Preview color={current.color} font={current.font} name={facility?.name || ''} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => save(null, null, null, null)} disabled={!data || saving || (color === null && font === null && layout === null && language === null)}>
          {t('qrTheme.reset')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => onClose(false)} disabled={saving}>{t('qrTheme.cancel')}</Button>
        <Button variant="contained" onClick={() => save(color, font, layout, language)} disabled={!data || saving}>
          {t('qrTheme.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
